import { DndContext, PointerSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/card'
import type { FormField } from '@/types/api/form'
import type { RecordListItem } from '@/types/api/record'
import { cn } from '@/utils/cn'

import { fieldChoices } from '../../utils/recordFilters'

import { rawValue, recordHref, recordTitle } from './viewUtils'

const NO_VALUE = '__none__'

function BoardCard({ record, draggable }: { record: RecordListItem; draggable: boolean }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: record.recordId, disabled: !draggable })
  return (
    <Card
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      className={cn('gap-1 p-3 text-sm shadow-none', draggable && 'cursor-grab', isDragging && 'opacity-40')}
    >
      <Link to={recordHref(record)} className="font-medium text-foreground hover:underline">
        {recordTitle(record)}
      </Link>
      {record.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {record.tags.map((tag) => (
            <span key={tag.tagId} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <span className="size-1.5 rounded-full" style={{ backgroundColor: tag.colorHex ?? 'var(--muted-foreground)' }} />
              {tag.label}
            </span>
          ))}
        </div>
      )}
    </Card>
  )
}

function BoardColumn({ id, label, records, canDrag }: { id: string; label: string; records: RecordListItem[]; canDrag: (record: RecordListItem) => boolean }) {
  const { setNodeRef, isOver } = useDroppable({ id })
  return (
    <div
      ref={setNodeRef}
      className={cn('flex w-64 shrink-0 flex-col gap-2 rounded-lg border border-border bg-muted/40 p-2', isOver && 'ring-2 ring-ring')}
    >
      <h3 className="flex items-center justify-between px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        <span className="truncate">{label}</span>
        <span>{records.length}</span>
      </h3>
      {records.map((record) => <BoardCard key={record.recordId} record={record} draggable={canDrag(record)} />)}
    </div>
  )
}

/** Cards in one column per choice of a SELECT/RADIO field; dragging a card changes that field. */
export function BoardView({ records, groupBy, onMove, canMove }: {
  records: RecordListItem[]
  groupBy: FormField | undefined
  onMove: (record: RecordListItem, field: FormField, value: string) => Promise<void>
  canMove: (record: RecordListItem, field: FormField) => boolean
}) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))
  if (!groupBy) {
    return <Card className="p-8 text-center text-sm text-muted-foreground shadow-none">Pick a Select or Radio field to group this board by.</Card>
  }
  const choices = fieldChoices(groupBy)
  const known = new Set(choices)
  const columns = [...choices.map((c) => ({ id: c, label: c })), { id: NO_VALUE, label: 'No value' }]
  const byColumn = (id: string) => records.filter((r) => {
    const value = rawValue(r, groupBy.key)
    return id === NO_VALUE ? !value || !known.has(value) : value === id
  })

  const handleDragEnd = (event: DragEndEvent) => {
    const record = records.find((r) => r.recordId === event.active.id)
    const target = event.over?.id as string | undefined
    if (!record || !target || !canMove(record, groupBy)) return
    const next = target === NO_VALUE ? '' : target
    if (next !== rawValue(record, groupBy.key)) onMove(record, groupBy, next).catch(() => {}) // the caller already toasts the failure
  }

  return (
    <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-2">
        {columns.map((c) => <BoardColumn key={c.id} id={c.id} label={c.label} records={byColumn(c.id)} canDrag={(r) => canMove(r, groupBy)} />)}
      </div>
    </DndContext>
  )
}
